import React from 'react';
import pluginPages from '../pluginPages';
import Spinner from '../shared/spinner/components/Spinner';
import { lazyComponent } from '../shared/util/lazyLoad';
import { pageKey } from './definitions';

/**
 * Plugin pages as components, loaded on demand.
 *
 * Each `pluginPages.js` entry names its page's file with `load`. This makes the component
 * from it, once per entry: the page's code loads the first time it renders, with the loading
 * ring showing until it arrives, and a tab left open across a deploy reloads once if the file
 * has gone (`shared/util/lazyLoad.js`).
 *
 * The code can also start loading before the page renders. The plugin route asks for it as
 * soon as it matches, so it downloads while the page waits for the plugin list; and the rail
 * asks for it when the pointer is over a menu item, so a click often finds it already there.
 */
const components = new WeakMap();

/** What a page shows while its code arrives: the loading ring, announced as loading. */
export const pageLoadingFallback = React.createElement(
  'span',
  { role: 'status', 'aria-label': 'Loading page' },
  React.createElement(Spinner)
);

export const componentFor = (entry) => {
  if (typeof entry?.load !== 'function') return null;
  let Component = components.get(entry);
  if (!Component) {
    Component = lazyComponent(entry.load, pageLoadingFallback);
    components.set(entry, Component);
  }
  return Component;
};

/** Starts loading a plugin page's code, if the page exists. Never throws. */
export const preloadPluginPage = (pluginName, pageName, pages = pluginPages) => {
  componentFor(pages[pageKey(pluginName, pageName)])?.preload();
};
